'use client'

import { ProductInfoSizes } from '@/app/[locale]/(landing-page)/wowbox/_components/productInfo-sizes'
import { Button } from '@/components/ui/button'
import { useAddToCart } from '@/hooks/react-query/cart/use-cart'
import { cn, stringToSlug } from '@/lib/utils'
import { Option, Product } from '@/types/product'
import { Loader2, Star } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AmazonProductInfoVariants } from './amazon-product-info-variants'
import { BestSellerProductPrice } from './best-seller-product-price'

interface Props {
  productData: Product
  badge: string
  description: React.ReactNode | string
}

export const BestSellerProductInteractive = ({
  productData,
  badge,
  description,
}: Props) => {
  const t = useTranslations()
  const router = useRouter()
  const [colorSelected, setColorSelected] = useState<string | null>(null)
  const [colorOptionActive, setColorOptionActive] = useState<Option | null>(
    null
  )
  const [selectedSize, setSelectedSize] = useState<string | null>(null)
  const { mutateAsync: addToCart, isPending: isAddingToCart } = useAddToCart()

  const handleColorSelect = useCallback((color: string, option: Option) => {
    setColorSelected(color)
    setColorOptionActive(option)
    setSelectedSize(null) // Reset size when color changes
  }, [])

  useEffect(() => {
    // Automatically select the first color on initial load
    const firstColorOption = productData?.options_value?.[0]?.options?.[0]
    if (firstColorOption) {
      handleColorSelect(stringToSlug(firstColorOption.title), firstColorOption)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productData])

  const handleSizeSelect = (size: string) => {
    setSelectedSize(size)
  }

  const hasSizeOptions = useMemo(() => {
    return productData.variants?.some(
      (v) => v.option2 && v.option2.trim() !== ''
    )
  }, [productData.variants])

  const selectedVariant = useMemo(() => {
    if (!colorSelected || !productData.variants) {
      return null
    }

    // Handle products with size options
    if (hasSizeOptions) {
      if (!selectedSize) return null
      return productData.variants.find(
        (variant) =>
          stringToSlug(variant.option1) === colorSelected &&
          variant.option2 === selectedSize
      )
    }

    // Handle products without size options (only color)
    return productData.variants.find(
      (variant) => stringToSlug(variant.option1) === colorSelected
    )
  }, [colorSelected, selectedSize, productData.variants, hasSizeOptions])

  const isAddToCartDisabled = useMemo(() => {
    // If no color is selected, disable
    if (!colorSelected) return true

    // If there are sizes but none is selected, disable
    if (hasSizeOptions && !selectedSize) return true

    // If a variant is selected, check its availability
    if (selectedVariant) {
      return selectedVariant.quantity <= 0 || !selectedVariant.available
    }

    // If no variant could be found for the selection, disable
    return true
  }, [selectedVariant, colorSelected, selectedSize, hasSizeOptions])

  const handleAddToCart = async () => {
    if (!selectedVariant || isAddToCartDisabled || isAddingToCart) {
      return
    }

    try {
      await addToCart({
        payload: {
          product_id: selectedVariant.product_id,
          quantity: 1,
          options: {
            id: selectedVariant.id,
          },
        },
        toastOptions: {
          productName: productData.title,
          variantName: `${colorOptionActive?.title || ''} / ${selectedSize || ''}`,
          regularPrice: selectedVariant.regular_price,
          comparePrice:
            selectedVariant.compare_price || productData.compare_price,
          imageUrl:
            colorOptionActive?.image?.[0]?.src ||
            productData.images?.[0]?.src ||
            '',
        },
      })
    } catch (error) {
      console.error('Error adding to cart:', error)
    }
  }

  if (!productData) return null

  return (
    <div className='flex flex-col gap-2 md:gap-4'>
      <div className='inline-block self-start rounded-full bg-orange-500 px-2 py-1 text-xs font-bold uppercase text-white md:text-sm'>
        {badge}
      </div>
      <h3 className='font-criteria text-lg md:text-3xl'>{productData.title}</h3>
      <div className='flex items-center gap-2 text-sm md:text-base'>
        <span>{productData.review?.ratingValue}</span>
        <div className='flex items-center'>
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              size={16}
              className={cn(
                'fill-yellow-500 text-yellow-500',
                i + 1 > (productData.review?.ratingValue || 0) &&
                  'fill-neutral-300 text-neutral-300'
              )}
            />
          ))}
        </div>
        <span className='text-neutral-500'>
          ({productData.review?.reviewCount} ratings)
        </span>
      </div>

      <AmazonProductInfoVariants
        product={productData}
        colorSelected={colorSelected}
        onColorSelect={handleColorSelect}
      />

      {colorSelected && (
        <ProductInfoSizes
          product={productData}
          colorSelected={colorSelected}
          selectedSize={selectedSize}
          onSizeSelect={handleSizeSelect}
          sizeChart={productData.sizechart || ''}
        />
      )}

      <div className='flex items-baseline gap-3'>
        <BestSellerProductPrice
          comparePrice={productData.compare_price}
          regularPrice={productData.regular_price}
          tags={productData.tags}
          product={productData}
        />
      </div>
      <div className='mt-2 flex flex-col-reverse gap-3 sm:flex-row'>
        <Button
          variant='outline-neutral'
          onClick={() => router.push(`/product/${productData.seo_alias}`)}
          className='w-full sm:w-auto'
        >
          {t('ldp_amazon_hardcode.highlight_product.view_detail')}
        </Button>
        <Button
          className='w-full bg-black text-white hover:bg-neutral-800 sm:w-auto'
          onClick={handleAddToCart}
          disabled={isAddToCartDisabled || isAddingToCart}
        >
          {isAddingToCart ? (
            <>
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              {t('ldp_amazon_hardcode.highlight_product.adding_to_cart')}
            </>
          ) : (
            <>
              {t('ldp_amazon_hardcode.highlight_product.add_to_cart')}
              <Image
                className='h-4 w-4'
                src='/icons/product/icon-cart.svg'
                width={16}
                height={16}
                alt='add cart icon'
              />
            </>
          )}
        </Button>
      </div>
      <p className='pl-2 text-xs text-neutral-500 md:pl-4 md:text-base'>
        {description}
      </p>
    </div>
  )
}
